/**
 * 사용량 — loginHS 로그인 업무 셸의 LLM 호출 집계 페이지(2026-09-14 사용자 요청: docuai 의 "사용량" 화면이 loginHS 에도 있어야 한다).
 * 2026-09-15 사용자 결정: 화면의 단위는 **크레딧**(호출당 고정 가중치 — 기본 1 · 정밀 20 · 첨부 +1), 원(₩)은 보이지 않는다.
 * 크레딧은 나중에 "크레딧 × 단가"로 청구하는 근거이고, 벤더 원가(원)는 관리자 집계(scope=all)에만 있다.
 * 위: 이번 달·오늘 모델별 호출 수·토큰·크레딧(로그인 사용자 자신). 아래: 최근 사용 내역(호출 건당 한 줄).
 * 원천은 `GET /api/onto/proposal/usage?days=60`(proposal_llm_call). 숫자를 만들지 않는다. 팔레트는 파랑·회색만.
 */
import React, { useEffect, useState } from "react"; // eslint-disable-line no-unused-vars
import { fetchUsage, resetAiQuota } from "./branchMapClient";

const MUTED = "var(--audit-text-muted)"; const LINE = "var(--audit-border)"; const ELEV = "var(--audit-bg-elevated, var(--audit-bg))";
const BLUE = "#1d4ed8"; const BLUE_BG2 = "#dbeafe"; const MONO = "JetBrains Mono, monospace";
// 2026-10-04 제안 LLM 을 오픈라우터 GPT-6.1 Sol 로 바꿨다 — gpt=기본, gpt-precise=정밀. 그 전 호출(gemini·opus)은 "이전 모델"로 남긴다.
const MODEL_LABEL = { gpt: "기본", "gpt-precise": "정밀", gemini: "기본 · 이전 모델", opus: "정밀 · 이전 모델", claude: "Claude(기타)", other: "기타" };
const MODEL_VENDOR = { gpt: "OpenAI", "gpt-precise": "OpenAI", gemini: "Gemini", opus: "Claude", claude: "Claude", other: "" };
const MODEL_ORDER = ["gpt", "gpt-precise", "gemini", "opus"];
const isPrecise = (k) => k === "opus" || k === "gpt-precise";
const KIND_LABEL = { signal: "후보 신호(제안 카드)", ask: "더 묻기", map: "분기 지도", other: "기타" };
const STATUS_LABEL = { ok: "정상", invalid: "응답 무효", unavailable: "실패", empty: "빈 응답" };
const fmtAt = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "");
const fmt = (n) => (n == null ? "–" : Number(n).toLocaleString("ko-KR"));

/** 크레딧 규칙 한 줄 — 서버 가중치를 그대로 읽는다(화면이 숫자를 만들지 않는다). */
function creditRule(w) {
  if (!w) return "크레딧은 호출당 고정 가중치입니다";
  return `크레딧은 호출당 고정 · 기본 ${fmt(w.basic)} · 정밀 ${fmt(w.precise)} · 첨부가 있으면 +${fmt(w.attachment)}`;
}

function PeriodCard({ title, sub, period, showCost, budget }) {
  const models = period?.models || {};
  const present = MODEL_ORDER.filter((k) => models[k]);
  const keys = [...(present.length ? present : ["gpt", "gpt-precise"]), ...Object.keys(models).filter((k) => !MODEL_ORDER.includes(k))];
  const pct = budget && budget.usedKrw != null ? Math.min(100, Math.round((budget.usedKrw / budget.monthlyKrw) * 100)) : null;
  return (
    <section data-testid={`usage-period-${title}`} style={{ border: `1px solid ${LINE}`, borderRadius: 10, background: ELEV, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <b style={{ fontSize: 14 }}>{title}</b>
        <span style={{ fontSize: 12, color: MUTED }}>{sub}</span>
        <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 13 }}>
          {fmt(period?.calls || 0)}회 · <b style={{ color: BLUE }}>{fmt(period?.credits || 0)}</b> 크레딧{showCost && period?.costKrw != null ? ` · 원가 ≈${fmt(period.costKrw)}원` : ""}
        </span>
      </div>
      {showCost && budget && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: MUTED }}>
            <span>월 원가 예산 {fmt(budget.monthlyKrw)}원 중 {fmt(budget.usedKrw)}원{budget.over ? " · 초과" : ""}</span>
            <span>{pct != null ? `${pct}%` : ""}</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: "var(--audit-bg-subtle, rgba(148,163,184,.14))", marginTop: 4, overflow: "hidden" }}>
            <div style={{ width: `${pct || 0}%`, height: "100%", background: budget.over ? "#8f98a5" : "#2563eb" }} />
          </div>
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, fontSize: 12.5 }}>
        <thead><tr style={{ color: MUTED, fontSize: 11.5 }}>
          <th style={{ textAlign: "left", padding: "4px 0", fontWeight: 500 }}>모델</th>
          <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 500 }}>호출</th>
          <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 500 }}>입력 토큰</th>
          <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 500 }}>출력 토큰</th>
          <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 500 }}>크레딧</th>
          {showCost && <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 500 }}>원가(원)</th>}
        </tr></thead>
        <tbody>
          {keys.map((k) => { const m = models[k] || { calls: 0, inTokens: 0, outTokens: 0, credits: 0, costKrw: null }; return (
            <tr key={k} data-testid={`usage-row-${k}`} style={{ borderTop: `1px solid ${LINE}` }}>
              <td style={{ padding: "6px 0" }} title={MODEL_VENDOR[k] || undefined}>{MODEL_LABEL[k] || k}</td>
              <td style={{ padding: "6px 0", textAlign: "right", fontFamily: MONO }}>{fmt(m.calls)}</td>
              <td style={{ padding: "6px 0", textAlign: "right", fontFamily: MONO }}>{fmt(m.inTokens)}</td>
              <td style={{ padding: "6px 0", textAlign: "right", fontFamily: MONO }}>{fmt(m.outTokens)}</td>
              <td style={{ padding: "6px 0", textAlign: "right", fontFamily: MONO, fontWeight: 600 }}>{fmt(m.credits || 0)}</td>
              {showCost && <td style={{ padding: "6px 0", textAlign: "right", fontFamily: MONO, color: MUTED }}>{m.costKrw != null ? fmt(m.costKrw) : "–"}</td>}
            </tr>); })}
        </tbody>
      </table>
    </section>
  );
}

export default function UsagePage({ fetchImpl, days = 60 }) {
  const [usage, setUsage] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    let alive = true;
    setState("loading");
    fetchUsage({ days, fetchImpl: fetchImpl || fetch }).then((u) => { if (!alive) return; setUsage(u); setState(u ? "ok" : "error"); });
    return () => { alive = false; };
  }, [days, fetchImpl]);
  // 원가(원)는 관리자 집계(scope=all)가 왔을 때만 — 사용자 화면에는 크레딧만.
  const showCost = Boolean(usage && usage.scope === "all" && usage.priced);
  return (
    <div data-testid="usage-page" style={{ maxWidth: 860, margin: "0 auto", padding: "18px 20px 40px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: 18, letterSpacing: "-.01em" }}>사용량</h1>
        <span style={{ fontSize: 12, color: MUTED }}>{usage && usage.scope === "all" ? "전체 사용자" : "내"} 제안 모드 LLM 호출(후보 신호 · 더 묻기 · 분기 지도) · 판정에는 들어가지 않는 제안 호출의 집계</span>
      </div>
      {state === "loading" && <div style={{ fontSize: 12.5, color: MUTED }}>집계를 불러오고 있습니다…</div>}
      {state === "error" && <div role="status" style={{ fontSize: 12.5, color: MUTED }}>집계를 불러오지 못했습니다. 로그인 상태와 연결을 확인해 주세요.</div>}
      {state === "ok" && usage && !usage.available && <div role="status" style={{ fontSize: 12.5, color: MUTED }}>집계 표가 아직 준비되지 않았습니다({usage.reason || "unavailable"}).</div>}
      {state === "ok" && usage && usage.available && (
        <>
          {usage.quota && usage.quota.enabled && (
            <section data-testid="usage-quota" style={{ border: `1px solid ${LINE}`, borderRadius: 10, background: ELEV, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <b style={{ fontSize: 14 }}>오늘 AI 사용</b>
              <span style={{ fontSize: 13 }}><b>{usage.quota.used}</b> / {usage.quota.limit}회</span>
              <span style={{ fontSize: 12, color: MUTED }}>AI 를 실제로 부른 횟수만 셉니다 · 한국 시각 자정에 새로 시작{usage.quota.resetsToday ? ` · 오늘 초기화 ${usage.quota.resetsToday}회` : ""}</span>
              {usage.quota.canReset && (
                <button type="button" data-testid="usage-quota-reset" onClick={async () => {
                  const q = await resetAiQuota({ fetchImpl: fetchImpl || fetch });
                  if (q) setUsage((u) => (u ? { ...u, quota: q } : u));
                }} style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 700, padding: "4px 12px", borderRadius: 6, border: "1px solid var(--audit-accent)", background: "transparent", color: "var(--audit-accent)", cursor: "pointer" }}>초기화</button>
              )}
            </section>
          )}
          <PeriodCard title="이번 달" sub={`${usage.month?.from || ""} ~ ${usage.month?.to || ""}`} period={usage.month} showCost={showCost} budget={usage.budget} />
          <PeriodCard title="오늘" sub={usage.asOf || ""} period={usage.today} showCost={showCost} budget={null} />
          <section style={{ border: `1px solid ${LINE}`, borderRadius: 10, background: ELEV, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <b style={{ fontSize: 14 }}>최근 사용 내역</b>
              <span style={{ fontSize: 12, color: MUTED }}>최근 {days}일 · 호출 건당 한 줄 · 최신순(최대 300건)</span>
            </div>
            {usage.history.length === 0
              ? <div style={{ fontSize: 12.5, color: MUTED, marginTop: 10 }}>아직 호출이 없습니다.</div>
              : (
                <table data-testid="usage-history" style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, fontSize: 12.5 }}>
                  <thead><tr style={{ color: MUTED, fontSize: 11.5 }}>
                    <th style={{ textAlign: "left", padding: "4px 0", fontWeight: 500 }}>시각</th>
                    <th style={{ textAlign: "left", padding: "4px 0", fontWeight: 500 }}>종류</th>
                    <th style={{ textAlign: "left", padding: "4px 0", fontWeight: 500 }}>모델</th>
                    <th style={{ textAlign: "left", padding: "4px 0", fontWeight: 500 }}>상태</th>
                    <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 500 }}>토큰 입력/출력</th>
                    <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 500 }}>크레딧</th>
                    {showCost && <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 500 }}>원가(원)</th>}
                  </tr></thead>
                  <tbody>
                    {usage.history.map((r, i) => (
                      <tr key={`${r.at}-${i}`} data-testid="usage-call" style={{ borderTop: `1px solid ${LINE}` }}>
                        <td style={{ padding: "7px 0", fontFamily: MONO, color: MUTED, whiteSpace: "nowrap" }}>{fmtAt(r.at)}</td>
                        <td style={{ padding: "7px 0" }}>{KIND_LABEL[r.kind] || r.kind}</td>
                        <td style={{ padding: "7px 0" }}><span title={[MODEL_VENDOR[r.model], r.modelName].filter(Boolean).join(" · ")} style={{ border: `1px solid ${LINE}`, borderRadius: 999, padding: "1px 8px", fontSize: 11.5, background: isPrecise(r.model) ? BLUE_BG2 : "transparent", color: isPrecise(r.model) ? BLUE : "inherit" }}>{MODEL_LABEL[r.model] || r.model}</span></td>
                        <td style={{ padding: "7px 0", color: r.status === "ok" ? "inherit" : MUTED }}>{STATUS_LABEL[r.status] || r.status || "–"}</td>
                        <td style={{ padding: "7px 0", textAlign: "right", fontFamily: MONO }}>{fmt(r.inTokens)} / {fmt(r.outTokens)}</td>
                        <td style={{ padding: "7px 0", textAlign: "right", fontFamily: MONO, fontWeight: 600 }} title="호출 때 확정한 크레딧 · 가중치가 바뀌어도 과거 값은 그대로">{r.credits != null ? fmt(r.credits) : "–"}</td>
                        {showCost && <td style={{ padding: "7px 0", textAlign: "right", fontFamily: MONO, color: MUTED }} title={r.settledKrw != null ? "정산 확정액" : "단가 추정치"}>{r.settledKrw != null ? fmt(r.settledKrw) : r.costKrw != null ? `≈${fmt(r.costKrw)}` : "–"}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </section>
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 12, lineHeight: 1.6 }}>
            <span data-testid="usage-credit-line">{creditRule(usage.creditWeights)}. 요금은 크레딧 × 단가로 청구되며 단가는 요금제에서 정합니다. 크레딧은 호출 때 확정되어 뒤에 가중치가 바뀌어도 과거 값은 그대로입니다.</span>
            {showCost && usage.prices && (
              <span data-testid="usage-price-line" title={usage.priceNote ? `근거 · ${usage.priceNote}` : undefined}>{" "}관리자 원가 단가(원/1천 토큰) · 기본 입력 {usage.prices.geminiIn} · 출력 {usage.prices.geminiOut} · 정밀 입력 {usage.prices.opusIn} · 출력 {usage.prices.opusOut}.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
